/**
 * Supplies the Greenhouse-specific UI capabilities.
 *
 * 1. The service defines Greenhouse labels and supported features.
 * 2. It parses Greenhouse Candidate URLs for the browser extension.
 * 3. The ATS service selector in `index.ts` supplies this service to consumers.
 */
/* eslint-disable modernloop/restrict-imports.cjs */
import React from 'react';

import { logError } from '@modernloop/shared/utils';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import GreenhouseOAuthCard from 'src/views-new/Settings/Integrations/Cards/GreenhouseOAuthCard';

import AtsSevice, {
  ExtensionSidebarParsedUrlType,
  IntegrationCardProps,
  ParsedAtsUrlResult,
  ScorecardGroupByStrategy,
  TScorecardSortKey,
} from './AtsService';

/** Gets the Greenhouse Candidate ID from a supported Candidate URL. */
const getCandidateIdFromUrl = (url: URL): number | null => {
  const folders = url.pathname.split('/') || [];
  const keywordIndex = folders.indexOf('people');
  if (keywordIndex < 0) {
    return null;
  }

  const pathParam = folders[keywordIndex + 1];
  if (!pathParam) {
    return null;
  }

  const id = parseInt(pathParam, 10);
  if (!Number.isNaN(id)) {
    return id;
  }

  return null;
};

/** Gets the Greenhouse Application ID from a supported Candidate URL. */
const getApplicationIdFromUrl = (url: URL): number | null => {
  const queryParam = url.searchParams.get('application_id') || null;
  if (queryParam) {
    const id = parseInt(queryParam, 10);
    if (!Number.isNaN(id)) {
      return id;
    }
  }

  const folders = url.pathname.split('/') || [];
  const keywordIndex = folders.indexOf('applications');
  if (keywordIndex < 0) {
    return null;
  }

  const pathParam = folders[keywordIndex + 1];
  if (!pathParam) {
    return null;
  }

  const id = parseInt(pathParam, 10);
  if (!Number.isNaN(id)) {
    return id;
  }

  return null;
};

/**
 * Defines the Greenhouse UI contract without exposing the ATS type to consumers.
 */
class GreenhouseAtsService implements AtsSevice {
  readonly featureFlag = null;

  readonly name = 'Greenhouse';

  readonly hostname = 'greenhouse'; // ex. https://app.greenhouse.io/

  readonly logoUrl = `/static/images/integrations/greenhouse.png`;

  readonly helpCenterUrl = HelpCenterUrl.GREENHOUSE_INTEGRATION;

  readonly scorecardLabelPluralTranslationKey = 'hiring-dashboard-common:common.ats.scorecardsFallback';

  readonly noScorecardLabelTranslationKey = 'hiring-dashboard-common:common.ats.noScorecardFallback';

  readonly scorecardSelectTitleTranslationKey = 'hiring-dashboard-nav:nav.scorecards.selectTitle';

  readonly scorecardSearchPlaceholderTranslationKey = 'hiring-dashboard-nav:nav.scorecards.searchPlaceholder';

  readonly scorecardSearchPlaceholderEllipsisTranslationKey =
    'hiring-dashboard-nav:nav.scorecards.searchPlaceholderEllipsis';

  readonly isSourcingLinkEnabled = true;

  readonly isAtsUserSelectEnabled = false;

  readonly isAtsImportAvailabilityEnabled = true;

  readonly skipInterviewKitLinkTokenValidation = false;

  readonly scorecardGroupByKey = null;

  readonly scorecardGroupByStrategy = ScorecardGroupByStrategy.AtsJobStage;

  readonly scorecardSortKey: TScorecardSortKey = 'index';

  readonly showEstimatedMinutes = true;

  // The sourcing-link flow must not create a test Candidate to check the legacy Greenhouse V1 permission.
  readonly createCandidatePermissionDialog = null;

  readonly showRequisitionId = true;

  readonly canSupportBrightHire = false;

  readonly hasScorecardSync = false;

  readonly scorecardSyncSettingLabelTranslationKey =
    'hiring-dashboard-common:common.ats.keepInterviewsInSyncWithAtsInterviewPlan';

  readonly hasHiringProcess = false;

  readonly hasScorecards = true;

  readonly hasEnforcedJobPermissions = true;

  readonly resetInterviewPlanWarningMessage =
    'The interview plan for this stage will be reset to its current settings in Greenhouse.';

  /**
   * Greenhouse connects through V3 OAuth only. The API-key integration type is gone with the
   * Harvest V1 shutdown, so there is one Greenhouse card and it is the OAuth card.
   */
  // eslint-disable-next-line class-methods-use-this
  getIntegrationCard = (props: IntegrationCardProps): JSX.Element => {
    return <GreenhouseOAuthCard disabled={props.disabled} onRemove={props.onRemove} />;
  };

  /** Parses a Greenhouse Candidate URL for the browser extension. */
  parseExtensionSidebarUrl = (externalUrl: string): ParsedAtsUrlResult | null => {
    try {
      const url: URL | undefined = new URL(externalUrl);
      // check if url is greenhouse
      if (!url.hostname.includes(this.hostname)) {
        return null;
      }

      const candidateId = getCandidateIdFromUrl(url);
      if (candidateId) {
        return {
          type: ExtensionSidebarParsedUrlType.Candidate,
          atsCandidateId: candidateId,
          atsApplicationId: getApplicationIdFromUrl(url),
        };
      }
    } catch (error) {
      logError('Error parsing Greenhouse ATS url', error);
    }

    return null;
  };
}

export default new GreenhouseAtsService();
